import { A } from '@solidjs/router';
import { For, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { orgHref } from '../lib/org';
import { useReviewRuns } from '../lib/repo-detail';
import { EmptyState } from './EmptyState';
import { QueryBoundary } from './QueryBoundary';
import { ReviewStatusPill } from './ReviewStatusPill';
import { Skeleton } from './Skeleton';
import { buttonClass } from './ui/Button';

// Repo detail → PR review runs (slice 06, #64). Read-only: the AI code-review
// runs for one PR, newest-first. Each run links to its live event timeline
// (`/fleet/runs/:run_id`) — the same durable stream the fleet audit view renders.
// Presenter; the data hook is `useReviewRuns` (lib/repo-detail).
//
// The status vocabulary moved to `<ReviewStatusPill/>` when the repo-wide Reviews
// tab started rendering the same runs: one `Record<TaskStatus, …>`, two surfaces.
// This view still carries only the task-side facts — the per-PR route serves no
// brain verdict, findings count or provenance, and inventing one here from the
// task row is the attribution guess the Reviews tab exists to avoid.

export type PrReviewRunsProps = {
  readonly prId: string;
  readonly token: string | null;
};

export function PrReviewRuns(props: PrReviewRunsProps): JSX.Element {
  const runs = useReviewRuns(() => props.prId, props.token);

  return (
    <section class="pr-review-runs" aria-label={t('repo_detail.review_runs_title')}>
      <h3 class="pr-review-runs__title">{t('repo_detail.review_runs_title')}</h3>
      <QueryBoundary
        query={runs}
        isEmpty={(rows) => rows.length === 0}
        loading={<Skeleton height="6rem" />}
        empty={<EmptyState message={t('repo_detail.review_runs_empty')} />}
      >
        {(rows) => (
          <ul class="pr-review-runs__list">
            <For each={rows}>
              {(run) => (
                <li class="pr-review-runs__item">
                  <ReviewStatusPill status={run.status} delivery={run.delivery} />
                  <code class="pr-review-runs__sha">{run.head_sha.slice(0, 7)}</code>
                  <span class="pr-review-runs__when">
                    {t('repo_detail.review_runs_when', { when: run.created_at.slice(0, 10) })}
                  </span>
                  <Show
                    when={run.run_id}
                    fallback={
                      <span class="pr-review-runs__queued">
                        {t('repo_detail.review_runs_not_started')}
                      </span>
                    }
                  >
                    {(runId) => (
                      <A
                        class={buttonClass({ variant: 'link' })}
                        href={orgHref(`/fleet/runs/${runId()}`)}
                      >
                        {t('repo_detail.review_runs_view')}
                      </A>
                    )}
                  </Show>
                </li>
              )}
            </For>
          </ul>
        )}
      </QueryBoundary>
    </section>
  );
}
